import { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if should show prompt (not shown in last 7 days)
    const lastPrompt = localStorage.getItem('pwa-prompt-dismissed');
    const shouldShow = !lastPrompt || (Date.now() - parseInt(lastPrompt)) > 7 * 24 * 60 * 60 * 1000;

    if (!standalone && shouldShow) {
      // For Android/Desktop, listen for beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
      };
      window.addEventListener('beforeinstallprompt', handler);

      // For iOS, show after a delay
      if (iOS) {
        setTimeout(() => setShowPrompt(true), 3000);
      }

      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="pwa-install-prompt fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-br from-amber-500/90 to-amber-600/90 backdrop-blur-lg rounded-xl shadow-2xl border border-amber-400/30 p-4 z-50 animate-in slide-in-from-bottom-4">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
      >
        <X className="w-4 h-4 text-white" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <img src="/images/videra-logo.png" alt="Videra Wealth" className="w-10 h-10 rounded-lg" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-sm">Install Videra Wealth</h3>
          <p className="text-white/80 text-xs mt-1">
            Add to your home screen for quick access and live portfolio updates
          </p>
          
          {isIOS ? (
            <div className="mt-3 p-2 bg-white/10 rounded-lg">
              <p className="text-white text-xs flex items-center gap-2">
                <span>Tap</span>
                <Share className="w-4 h-4" />
                <span>then</span>
                <span className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
                  <Plus className="w-3 h-3" /> Add to Home Screen
                </span>
              </p>
            </div>
          ) : (
            <Button 
              onClick={handleInstall}
              size="sm"
              className="mt-3 bg-white text-amber-600 hover:bg-white/90 font-semibold"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
